<template>
  <div
    class="clara-aurora"
    aria-hidden="true"
  >
    <span class="clara-aurora__veu clara-aurora__veu--1"></span>
    <span class="clara-aurora__veu clara-aurora__veu--2"></span>
    <span class="clara-aurora__veu clara-aurora__veu--3"></span>
  </div>
</template>

<script>
/**
 * Aurora de fundo, ancorada na BORDA DE BAIXO.
 *
 * É a orientação invertida do efeito original: no shader do Vue Bits a
 * intensidade cresce com `uv.y`, então a cortina nasce em cima. Aqui ela nasce
 * embaixo, encostada no campo de digitação, e se dissolve para cima — que é a
 * leitura certa para algo que sai de onde o operador escreve.
 *
 * Feito em CSS pelo mesmo motivo do ClaraStrands: sem dependência, e o
 * `prefers-reduced-motion` sai de graça.
 *
 * Diferença importante para o ClaraStrands: aquele usa `mix-blend-mode: screen`
 * e só aparece sobre superfície escura. Este vive sobre o corpo CLARO do painel,
 * então compõe por alpha comum — nada de blend aditivo, que sobre branco não
 * renderiza nada.
 */
export default {
  name: 'ClaraAurora'
};
</script>

<style scoped>
.clara-aurora {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  /* A máscara é o que "cola" a aurora no input: opaca embaixo, sumindo para
     cima. Sem ela o retângulo apareceria como uma faixa com borda dura. */
  -webkit-mask-image: linear-gradient(to top, #000 0%, rgba(0, 0, 0, 0.55) 42%, transparent 100%);
  mask-image: linear-gradient(to top, #000 0%, rgba(0, 0, 0, 0.55) 42%, transparent 100%);
}

/* Cada véu é uma mancha larga e muito borrada. O borrão é o efeito: são três
   elipses, o que dá a ondulação é elas se cruzarem em ritmos diferentes. */
.clara-aurora__veu {
  position: absolute;
  bottom: -60%;
  height: 190%;
  width: 62%;
  border-radius: 50%;
  filter: blur(26px);
  opacity: 0.55;
}

.clara-aurora__veu--1 {
  left: -12%;
  background: radial-gradient(closest-side, var(--clara-brand) 0%, transparent 100%);
  animation: clara-aurora-desliza-1 11s ease-in-out infinite;
}

.clara-aurora__veu--2 {
  left: 30%;
  background: radial-gradient(closest-side, var(--clara-brand-soft) 0%, transparent 100%);
  opacity: 0.7;
  animation: clara-aurora-desliza-2 14s ease-in-out infinite;
}

.clara-aurora__veu--3 {
  left: 62%;
  background: radial-gradient(closest-side, var(--clara-brand) 0%, transparent 100%);
  animation: clara-aurora-desliza-3 19s ease-in-out infinite;
}

/* Durações coprimas entre si (11, 14, 19): os três nunca reencontram a mesma
   posição, então não se percebe o ciclo.

   O curso e as durações foram abertos depois de medir o efeito anterior: com
   17s/23s/29s e deslocamento de ~15%, o véu andava cerca de 13px por segundo
   num retângulo de 92px de altura e 26px de desfoque — o movimento existia, mas
   ficava abaixo do que se percebe, e a aurora era lida como uma mancha parada.

   A opacidade entra no keyframe junto com o transform de propósito: o desfoque
   engole boa parte do deslocamento, e é a variação de INTENSIDADE que faz o
   efeito parecer vivo. Ela substitui a opacidade base de cada véu. */
@keyframes clara-aurora-desliza-1 {
  0%,
  100% {
    transform: translate3d(-16%, 2%, 0) scaleY(0.94);
    opacity: 0.4;
  }
  50% {
    transform: translate3d(22%, -9%, 0) scaleY(1.36);
    opacity: 0.68;
  }
}

@keyframes clara-aurora-desliza-2 {
  0%,
  100% {
    transform: translate3d(18%, -6%, 0) scaleY(1.24);
    opacity: 0.72;
  }
  50% {
    transform: translate3d(-20%, 3%, 0) scaleY(0.86);
    opacity: 0.44;
  }
}

@keyframes clara-aurora-desliza-3 {
  0%,
  100% {
    transform: translate3d(12%, 1%, 0) scaleY(0.9);
    opacity: 0.38;
  }
  50% {
    transform: translate3d(-24%, -11%, 0) scaleY(1.4);
    opacity: 0.66;
  }
}

/* Parada ela continua valendo como um brilho suave acima do campo. */
@media (prefers-reduced-motion: reduce) {
  .clara-aurora__veu {
    animation: none;
  }
}
</style>
